import {
  createAllRestaurantsOption,
  isAllRestaurantsSelected,
  useAppUserDefaultStore,
} from '../../stores/appUserDefaultStore';
import { useRestaurantStore } from '../../stores/appRestaurantStore';

/**
 * Restaurant selection for tour steps whose control only exists in one of the two
 * modes: the products table's supplier filter needs a single restaurant, the group
 * spend total needs the combined view. A step sets the state it needs rather than
 * skipping itself, so the user sees the thing the copy is describing.
 *
 * These write to the store only. Persisting through `saveDefaultRestaurant` would
 * move the account's stored default restaurant as a side effect of taking a tour,
 * so the change lasts for the session and no further.
 */

const POLL_MS = 80;
const MAX_ATTEMPTS = 40;

/** Retry until the restaurant list has loaded, then stop. Returns a cleanup for onEnter. */
function untilReady(pick: () => boolean): (() => void) | void {
  if (pick()) return;
  let attempts = 0;
  const id = window.setInterval(() => {
    attempts += 1;
    if (pick() || attempts > MAX_ATTEMPTS) window.clearInterval(id);
  }, POLL_MS);
  return () => window.clearInterval(id);
}

/** Switch to the combined view. A single-restaurant account has no such option. */
export function ensureAllRestaurantsSelected(): (() => void) | void {
  return untilReady(() => {
    const defaults = useAppUserDefaultStore.getState();
    if (isAllRestaurantsSelected(defaults.restaurant)) return true;
    const restaurants = useRestaurantStore.getState().restaurants;
    if (restaurants.length < 2) return false;
    defaults.setRestaurant(createAllRestaurantsOption(defaults.restaurant?.userId ?? restaurants[0].userId));
    return true;
  });
}

/** Drop out of the combined view onto the first restaurant the user can see. */
export function ensureRestaurantSelected(): (() => void) | void {
  return untilReady(() => {
    const defaults = useAppUserDefaultStore.getState();
    if (defaults.restaurant && !isAllRestaurantsSelected(defaults.restaurant)) return true;
    const first = useRestaurantStore.getState().restaurants[0];
    if (!first) return false;
    defaults.setRestaurant(first);
    return true;
  });
}

/** Steps that talk about switching location only make sense with more than one. */
export function hasMultipleRestaurants(): boolean {
  return useRestaurantStore.getState().restaurants.length > 1;
}

/**
 * The user has landed on the combined view. `isAllRestaurantsSelected` already reads
 * a null selection as false, so this is safe before the restaurants have loaded.
 */
export function isViewingAllRestaurants(): boolean {
  return isAllRestaurantsSelected(useAppUserDefaultStore.getState().restaurant);
}

/** Pick a different real location once per step, keeping the instruction stable after selection. */
let requestedRestaurant: { id: string; name: string } | undefined;
export function prepareRestaurantSwitch(): () => void {
  const selectedId = useAppUserDefaultStore.getState().restaurant?.id;
  requestedRestaurant = useRestaurantStore.getState().restaurants.find(restaurant => restaurant.id !== selectedId);
  return () => { requestedRestaurant = undefined; };
}
export function getRequestedRestaurantValues(): Record<string, string> {
  if (!requestedRestaurant) prepareRestaurantSwitch();
  return { restaurant: requestedRestaurant?.name ?? '' };
}
export function requestedRestaurantSelected(): boolean {
  return Boolean(requestedRestaurant && useAppUserDefaultStore.getState().restaurant?.id === requestedRestaurant.id);
}
export function getSelectedRestaurantValues(): Record<string, string> {
  return { restaurant: useAppUserDefaultStore.getState().restaurant?.name ?? '' };
}
